import { flow, getEnv, Instance, types } from "mobx-state-tree";
import { activityPricesStorage, ActivityTypes, getActivityLabel } from "../shop/ActivityPricesStorage";
import { IServiceStorageEnv, ServiceStorage } from "./ServiceStorage";
import { ShopHistoryStorageModel } from "../shop/ShopHistoryStorage";
import { ConversionServiceModel } from "../conversion/VideoConversionService";
import { SegmentationServiceModel } from "../segmentation/SegmentationService";
import { AnalysisServiceModel } from "../analysis/AnalysisService";
import { OcrServiceModel } from "../ocr/OcrService";
import { RestoreDataServiceModel } from "../restore-data/RestoreDataService";
import { FileUploadTracker } from "../upload/FileUploadTracker";
import { endpoints } from "../api/ApiEndpoints";

// Field order matters: serviceList (Object.values) preserves this order, and the app runs
// its services in that order for startService / runAfterUpload / redo. videoAnalysis MUST come
// before videoSegmentation so that on the combined Analysis+Segmentation app, analysis is
// triggered first. The backend then defers segmentation until analysis finishes — analysis and
// segmentation of one file cannot be submitted together (they collide on the Job Manager's
// shared task-ID). See FileEditor.TriggerVideoSegmentation / VideoProcessingFinalizer.
const ServicesModel = types.model({
    videoConversion: types.optional(types.maybe(ConversionServiceModel), undefined),
    videoAnalysis: types.optional(types.maybe(AnalysisServiceModel), undefined),
    videoSegmentation: types.optional(types.maybe(SegmentationServiceModel), undefined),
    ocrService: types.optional(types.maybe(OcrServiceModel), undefined),
    restoreDataService: types.optional(types.maybe(RestoreDataServiceModel), undefined),
})

export const AppStorageModel = types.model({
    step: types.optional(types.number, 1),
    collapsed: types.optional(types.boolean, false),
    services: ServicesModel,
    redoSelection: types.optional(types.frozen<ActivityTypes[]>(), []),
    fileId: types.optional(types.frozen<number | undefined>(), undefined),
    fileGuid: types.optional(types.frozen<string | undefined>(), undefined),
    route: types.string,
    activityType: types.frozen<ActivityTypes[]>(),
    image: types.string,
    titleKey: types.string,
    descriptionkey: types.string,
    navigate: types.optional(types.string, ""),
    uploadAllFileTypes: types.optional(types.boolean, false),
    // When set, the app's history/usage list matches files whose service set is
    // ANY of these sets (via the backend allowedServiceSets filter) instead of an
    // exact match on activityType. Used so Inspection Insights also lists files
    // run as OCR + Analysis together (service set [VideoOcr, VideoAnalysis]),
    // which the exact-set serviceFilter would otherwise hide.
    historyServiceSets: types.optional(types.frozen<ActivityTypes[][] | undefined>(), undefined),
    errorMessage: types.optional(types.frozen<string>(), "")
}).views((self) => ({
    get canRun() {
        return getEnv<IServiceStorageEnv>(self).uploadFilesService.filesForUpload.length !== 0;
    },
    get fileRoute() {
        return self.route + "?guid=" + self.fileGuid;
    },
    get historyStorage() {
        var api = getEnv<IServiceStorageEnv>(self).api;
        // allowedServiceSets and serviceFilter are ANDed by the backend, so when
        // historyServiceSets is configured we use it INSTEAD of serviceFilter.
        if (self.historyServiceSets !== undefined) {
            return ShopHistoryStorageModel.create({allowedServiceSets: self.historyServiceSets, clickPath: self.route + "?guid=:guid", clientSidePagination: true, useGridFiltering: true}, {api});
        }
        return ShopHistoryStorageModel.create({serviceFilter: self.activityType, clickPath: self.route + "?guid=:guid", clientSidePagination: true, useGridFiltering: true}, {api});
    },
    get serviceList(): ServiceStorage[] {
        return Object.values(self.services).filter((s: ServiceStorage) => s !== undefined);
    },
    get isLoading() {
        return this.serviceList.some(service => service === undefined ? false : service.isLoading);
    },
    get redoableServices(): {label: string, service: ActivityTypes}[] {
        return this.serviceList.map((service: ServiceStorage) => {
            return {label: getActivityLabel([service.activityType]), service: service.activityType}
        })
    },
    get redoPrice() {
        return activityPricesStorage.getPrices(self.redoSelection);
    }
})).actions((self) => ({
    setError(message: string) {
        self.errorMessage = message;
    },
}))
.actions((self) => ({
    clear: () => {
        self.fileId = undefined;
        self.serviceList.forEach((element: ServiceStorage) => {
            element.clear();
        });
    },
    setStep(step: number) {
        self.step = step;
    },
    toggleCollapsed(collapsed?: boolean) {
        self.collapsed = collapsed || !self.collapsed;
    },
    getFileId: flow(function* (guid: string) {
        const endpoint = endpoints.get("file-id");
        endpoint.routeParams = [guid!];
        return yield getEnv<IServiceStorageEnv>(self).api.get(endpoint);

    }),
    getFileGuid: flow(function* (id: number) {
        const endpoint = endpoints.get("file-guid");
        endpoint.routeParams = [id!];
        return yield getEnv<IServiceStorageEnv>(self).api.get(endpoint);
    }),
    setFileId(fileId: number) {
        self.fileId = fileId;
        self.serviceList.forEach((element: ServiceStorage) => {
            element.setFileId(fileId);
        });
    },
    setFileGuid(fileGuid: string) {
        self.fileGuid = fileGuid;
        self.serviceList.forEach((element: ServiceStorage) => {
            element.setFileGuid(fileGuid);
        });
    },
    toggleRedoSelection(service: ActivityTypes) {
        if (!self.redoSelection.includes(service)) {
            var temp = [...self.redoSelection];
            temp.push(service);
            self.redoSelection = temp;
        } else {
            var temp = [...self.redoSelection];
            self.redoSelection = temp.filter((a: ActivityTypes) => a !== service)
        }
    },
    load: flow(function* () {
        if (self.fileId === undefined) return;
        // A single failed GET (token refresh, API cold start, blob briefly unavailable while
        // a job writes it) used to replace the whole page with "general.apps.error" until
        // the next successful reload cleared it. Retry each service once before giving up,
        // and decide the error for the whole pass so one service's success can't mask
        // another's failure.
        var failed = false;
        for (var service of self.serviceList) {
            try {
                yield service.load();
            } catch (e: any) {
                console.log(e)
                yield new Promise(resolve => setTimeout(resolve, 2000));
                try {
                    yield service.load();
                } catch (retryError: any) {
                    console.log(retryError)
                    failed = true;
                }
            }
        }
        self.setError(failed ? "general.apps.error" : "")
    }),
    startService: flow(function* () {
        if (self.fileId === undefined) return;
        for (var service of self.serviceList) {
            yield service.startService();
        }
    }),
    runAfterUpload: flow(function* () {
        for (var service of self.serviceList) {
            yield service.runAfterUpload();
        }
    }),
    setNavigate(path: string) {
        self.navigate = path;
    },
    resetNavigate() {
        self.navigate = "";
    }
})).actions((self) => ({
    redo: flow(function* () {
        for (var service of self.serviceList) {
            if (self.redoSelection.includes(service.activityType)) yield service.redo();
        }
    }),
    new: flow(function* () {
        self.fileId = undefined;
        for (var service of self.serviceList) {
            yield service.new();
        }
        getEnv<IServiceStorageEnv>(self).uploadFilesService.clear();
        self.setNavigate(self.route);
    }),
    onUploadFinished: flow(function* (file: FileUploadTracker) {
        file.completed();
        self.setFileId(file.id!);
        self.setFileGuid(yield self.getFileGuid(file.id!));
        yield self.runAfterUpload();
        yield self.load();
        yield getEnv<IServiceStorageEnv>(self).creditBalanceStorage.load();
        // Restore stays on its own page to show the Restored/Failed result step
        // (the user then opens the restored file in Inspection Insights from there).
        const restoreService: any = self.services.restoreDataService;
        if (restoreService === undefined) {
            self.setNavigate(self.fileRoute);
        }
    }),
    onConversionFinished: flow(function*(id: number) {
        if (id == self.fileId) yield self.load();
    }),
})).actions((self) => ({
    upload() {
        getEnv<IServiceStorageEnv>(self).uploadFilesService.startStandaloneUpload(self.onUploadFinished, self.uploadAllFileTypes);
    }
})).actions((self) => {
    return {
        afterCreate() {
            self.load();
        }
    }
})

export type AppStorage = Instance<typeof AppStorageModel>;